import React, { useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import CreditReportingPage from "./credit-reporting.page";
import {
    setActiveTab,
    setPeriodFilter,
    setSelectedMonth,
    initReportingCredit,
} from "modules/reporting/redux/slice/reportingCredit.slice";
import {
    getOpeningBalance,
    getAllocatedCredits,
    getCreditsUsed,
    getCreditsReimbursed,
    getClosingBalance,
    getEligibleRollover,
    getTotalPrintJobs,
    getUtilizationRate,
    getMonthlyCreditUsage,
    getCreditsUsedVsReimbursed,
    getPrintedVsRequestedQuantity,
} from "modules/reporting/redux/action";
import { getLang } from "../../../../app/feature/constants";

export default function CreditReportingContainer({ language }) {
    const dispatch = useDispatch();
    const lang = useSelector(state => state.constant.languages);
    const translate = (key) => getLang(lang, key);

    // Get state from Redux
    const {
        executiveOverview,
        monthlyUsageData,
        creditsUsedVsReimbursedData,
        printedVsRequestedData,
        activeTab,
        periodFilter,
        selectedMonth,
    } = useSelector((state) => state.reporting.credit);

    // Get date range from selected month
    const getDateRangeFromMonth = useCallback((monthFilter) => {
        const date = moment().year(monthFilter.year).month(monthFilter.month);
        return {
            start_date: date.startOf('month').format('YYYY-MM-DD'),
            end_date: date.endOf('month').format('YYYY-MM-DD'),
        };
    }, []);

    // Fetch all executive overview data
    const fetchExecutiveOverviewData = useCallback((monthFilter) => {
        const { start_date, end_date } = getDateRangeFromMonth(monthFilter);

        dispatch(getOpeningBalance({ start_date, end_date }));
        dispatch(getAllocatedCredits({ start_date, end_date }));
        dispatch(getCreditsUsed({ start_date, end_date }));
        dispatch(getCreditsReimbursed({ start_date, end_date }));
        dispatch(getClosingBalance({ start_date, end_date }));
        dispatch(getEligibleRollover({ start_date, end_date }));
        dispatch(getTotalPrintJobs({ start_date, end_date }));
        dispatch(getUtilizationRate({ start_date, end_date }));
        dispatch(getMonthlyCreditUsage({ start_date, end_date }));
        dispatch(getCreditsUsedVsReimbursed({ start_date, end_date }));
        dispatch(getPrintedVsRequestedQuantity({ start_date, end_date, period: periodFilter }));
    }, [dispatch, getDateRangeFromMonth, periodFilter]);

    // Initialize on mount
    useEffect(() => {
        dispatch(initReportingCredit());
        fetchExecutiveOverviewData(selectedMonth);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch]);

    const handleTabChange = (newTab) => {
        dispatch(setActiveTab(newTab));
        // Refresh data when switching to Executive Overview tab
        if (newTab === 0) {
            fetchExecutiveOverviewData(selectedMonth);
        }
    };

    const handlePeriodFilterChange = (newPeriod) => {
        dispatch(setPeriodFilter(newPeriod));
        const { start_date, end_date } = getDateRangeFromMonth(selectedMonth);
        dispatch(getPrintedVsRequestedQuantity({ start_date, end_date, period: newPeriod }));
    };

    const handleMonthChange = (newMonth) => {
        dispatch(setSelectedMonth(newMonth));
        fetchExecutiveOverviewData(newMonth);
    };

    const handleExport = () => {
        // TODO: Implement export functionality
        console.log("Exporting executive overview data...");
    };

    return (
        <CreditReportingPage
            executiveOverview={executiveOverview}
            monthlyUsageData={monthlyUsageData}
            creditsUsedVsReimbursedData={creditsUsedVsReimbursedData}
            printedVsRequestedData={printedVsRequestedData}
            activeTab={activeTab}
            periodFilter={periodFilter}
            selectedMonth={selectedMonth}
            onTabChange={handleTabChange}
            onPeriodFilterChange={handlePeriodFilterChange}
            onMonthChange={handleMonthChange}
            onExport={handleExport}
            translate={translate}
            language={language}
        />
    );
}
